import React, { useEffect, useState } from "react";
import Table from "@cloudscape-design/components/table";
import Box from "@cloudscape-design/components/box";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Button from "@cloudscape-design/components/button";
import TextFilter from "@cloudscape-design/components/text-filter";
import Header from "@cloudscape-design/components/header";
import Pagination from "@cloudscape-design/components/pagination";
import Input from "@cloudscape-design/components/input";
import Select from "@cloudscape-design/components/select";
import Toggle from "@cloudscape-design/components/toggle";
import Tabs from "@cloudscape-design/components/tabs";
import {
  ContentLayout,
  Modal,
  Container,
  Spinner,
  Badge,
  StatusIndicator,
} from "@cloudscape-design/components";
import { useTranslation } from "react-i18next";
import {
  users,
  createUser,
  deleteUser,
  getAllRolesPermissions,
  updatePermissions,
  getPermissionsAudit,
  updateUserRole,
} from "../api/api";
import { validateEmail, validateUsername } from "../util/validators";
import { useUserRole } from "../api/role";
import AlertNotification from "../components/AlertNotification";

function Users() {
  const { t } = useTranslation();
  const { hasPermission } = useUserRole();
  const [selectedItems, setSelectedItems] = useState([]);
  const [tableItems, setTableItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    role: "Admin",
  });
  const [newUserErrors, setNewUserErrors] = useState({ name: "", email: "" });
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [isDeletingUser, setIsDeletingUser] = useState(false);
  const [alert, setAlert] = useState(null);
  const clearAlert = () => setAlert(null);
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [filteredItems, setFilteredItems] = useState([]);
  const [filteringText, setFilteringText] = useState("");
  const [loading, setLoading] = useState(true);
  const [activeTabId, setActiveTabId] = useState("users");

  const [featureFlags, setFeatureFlags] = useState({});
  const [loadingFeatureFlags, setLoadingFeatureFlags] = useState(false);
  const [updatingPermissions, setUpdatingPermissions] = useState(false);
  const [updatingUserRole, setUpdatingUserRole] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);

  const [auditLog, setAuditLog] = useState([]);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [auditCurrentPage, setAuditCurrentPage] = useState(1);
  const [auditFilteringText, setAuditFilteringText] = useState("");
  const [auditSortingColumn, setAuditSortingColumn] = useState({
    sortingColumn: { sortingField: "timestamp" },
    sortingDescending: true,
  });

  const PAGE_SIZE = 5;
  const AUDIT_PAGE_SIZE = 10;

  const roleOptions = [
    { value: "Admin", label: "Admin" },
    { value: "Analista", label: "Analista" },
    { value: "Prompting", label: "Prompting" },
    { value: "Ejecutivo", label: "Ejecutivo" },
    { value: "Invitado", label: "Invitado" },
  ];

  const availableFeatures = [
    {
      id: "view_dashboard",
      name: "Ver Dashboard",
      description: "Acceso al dashboard principal",
    },
    {
      id: "manage_users",
      name: "Gestionar Usuarios",
      description: "Crear, editar y eliminar usuarios",
    },
    {
      id: "manage_prompts",
      name: "Gestionar Prompts",
      description: "Crear y editar prompts de análisis",
    },
    {
      id: "manage_audios",
      name: "Gestionar Audios",
      description: "Subir y gestionar archivos de audio",
    },
    {
      id: "view_insights",
      name: "Ver Insights",
      description: "Acceso a análisis e insights",
    },
    {
      id: "manage_prompts_refresh",
      name: "Refrescar Prompts",
      description: "Regenerar análisis con nuevos prompts",
    },
    {
      id: "view_dashboard_metrics",
      name: "Ver Métricas Dashboard",
      description: "Visualizar métricas del dashboard",
    },
    {
      id: "view_quality_insights",
      name: "Ver Insights Calidad",
      description: "Análisis de calidad de llamadas",
    },
    {
      id: "view_voc_insights",
      name: "Ver Insights VOC",
      description: "Análisis de voz del cliente",
    },
    {
      id: "view_genai_insights",
      name: "Ver Insights GenAI",
      description: "Análisis generados por IA",
    },
    {
      id: "view_cx_insights",
      name: "Ver Insights CX",
      description: "Análisis de experiencia del cliente",
    },
    {
      id: "manage_regex_patterns",
      name: "Gestionar Patrones Regex",
      description: "Gestionar patrones de expresiones regulares",
    },
  ];

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await users();
      setTableItems(response);
    } catch (err) {
      setAlert({ type: "error", message: err.message });
    } finally {
      setLoading(false);
    }
  };

  const fetchFeatureFlags = async () => {
    try {
      setLoadingFeatureFlags(true);
      const response = await getAllRolesPermissions();
      setFeatureFlags(response);
    } catch (err) {
      console.error("Error al cargar feature flags:", err);
      setAlert({ type: "error", message: "Error al cargar los feature flags" });
    } finally {
      setLoadingFeatureFlags(false);
    }
  };

  const fetchAuditLog = async () => {
    try {
      setLoadingAudit(true);
      const response = await getPermissionsAudit();
      setAuditLog(response.items || []);
    } catch (err) {
      setAlert({
        type: "error",
        message: "Error al cargar el log de auditoría",
      });
    } finally {
      setLoadingAudit(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    if (hasPermission("manage_users")) {
      fetchFeatureFlags();
      fetchAuditLog();
    }
  }, [hasPermission]);

  useEffect(() => {
    if (filteringText) {
      const lowerFilteringText = filteringText.toLowerCase();
      const filtered = tableItems.filter(
        (item) =>
          item.name.toLowerCase().includes(lowerFilteringText) ||
          item.email.toLowerCase().includes(lowerFilteringText) ||
          item.role.toLowerCase().includes(lowerFilteringText)
      );
      setFilteredItems(filtered);
    } else {
      setFilteredItems(tableItems);
    }
    setCurrentPageIndex(1);
  }, [filteringText, tableItems]);

  const pagesCount = Math.ceil(filteredItems.length / PAGE_SIZE);
  const startIndex = (currentPageIndex - 1) * PAGE_SIZE;
  const paginatedItems = filteredItems.slice(
    startIndex,
    startIndex + PAGE_SIZE
  );

  const handleDelete = async () => {
    try {
      setIsDeletingUser(true);
      await deleteUser(userToDelete.name);
      setShowModal(false);
      await fetchUsers();
      setAlert({ type: "success", message: t("users.deleteSuccess") });
      setUserToDelete(null);
    } catch (err) {
      setAlert({ type: "error", message: err.message });
    } finally {
      setIsDeletingUser(false);
    }
  };

  const validateField = (key, value) => {
    const duplicate = tableItems.find(
      (item) => item[key].toLowerCase() === value.trim().toLowerCase()
    );
    return duplicate ? `${t("users." + key)} ya existe` : "";
  };

  const handleInputChange = (key, value) => {
    setNewUser((prev) => ({ ...prev, [key]: value }));
    if (key === "email") {
      const duplicateError = validateField(key, value);
      const formatError = validateEmail(value);
      setNewUserErrors((prev) => ({
        ...prev,
        [key]: duplicateError || formatError,
      }));
    }
    if (key === "name") {
      const duplicateError = validateField(key, value);
      const formatError = validateUsername(value, t);
      setNewUserErrors((prev) => ({
        ...prev,
        [key]: duplicateError || formatError,
      }));
    }
  };

  const handleAddUser = async () => {
    if (
      newUserErrors.name ||
      newUserErrors.email ||
      !newUser.name.trim() ||
      !newUser.email.trim()
    ) {
      return;
    }

    const cleanUser = {
      name: newUser.name.trim(),
      email: newUser.email.trim(),
      role: newUser.role,
    };

    setIsAddingUser(true);
    try {
      await createUser(cleanUser);
      await fetchUsers();
      setShowAddUserModal(false);
      setAlert({ type: "success", message: t("users.createSuccess") });
    } catch (err) {
      setAlert({ type: "error", message: t("users.createError") });
      setShowAddUserModal(false);
    } finally {
      setIsAddingUser(false);
    }
  };

  const handlePermissionToggle = async (role, featureId, enabled) => {
    if (role === "Admin") {
      setAlert({
        type: "warning",
        message: "Los permisos de Administrador no pueden ser modificados",
      });
      return;
    }

    try {
      setUpdatingPermissions(true);

      const currentPermissions = featureFlags[role]?.permissions || [];
      let newPermissions;

      if (enabled) {
        newPermissions = currentPermissions.includes(featureId)
          ? currentPermissions
          : [...currentPermissions, featureId];
      } else {
        newPermissions = currentPermissions.filter((p) => p !== featureId);
      }

      await updatePermissions({
        role,
        permissions: newPermissions,
      });

      setFeatureFlags((prev) => ({
        ...prev,
        [role]: {
          ...prev[role],
          permissions: newPermissions,
        },
      }));

      await fetchAuditLog();

      setAlert({
        type: "success",
        message: `Permiso ${
          enabled ? "habilitado" : "deshabilitado"
        } correctamente para ${role}`,
      });
    } catch (err) {
      setAlert({ type: "error", message: "Error al actualizar permisos" });
    } finally {
      setUpdatingPermissions(false);
    }
  };

  const formatAuditAction = (action) => {
    const actionMap = {
      PERMISSION_ADDED: "Permiso agregado",
      PERMISSION_REMOVED: "Permiso removido",
    };
    return actionMap[action] || action;
  };

  const getActionStatusType = (action) => {
    switch (action) {
      case "PERMISSION_ADDED":
        return "success";
      case "PERMISSION_REMOVED":
        return "error";
      default:
        return "info";
    }
  };

  const handleRoleChange = async (username, newRole) => {
    try {
      setUpdatingUserRole(true);
      setEditingUserId(username);
      
      await updateUserRole({ username, newRole });
      await fetchUsers();
      
      setAlert({
        type: "success",
        message: `Rol actualizado correctamente para ${username}`
      });
    } catch (err) {
      setAlert({ 
        type: "error", 
        message: `Error al actualizar el rol: ${err.message}` 
      });
    } finally {
      setUpdatingUserRole(false);
      setEditingUserId(null);
    }
  };

  const filteredAuditItems = auditFilteringText
    ? auditLog.filter((item) =>
        Object.values(item).some((value) =>
          String(value).toLowerCase().includes(auditFilteringText.toLowerCase())
        )
      )
    : auditLog;

  const sortedAuditItems = [...filteredAuditItems].sort((a, b) => {
    const { sortingColumn, sortingDescending } = auditSortingColumn;
    const field = sortingColumn?.sortingField || "timestamp";
    
    let valueA = a[field];
    let valueB = b[field];

    if (field === "timestamp") {
      valueA = new Date(valueA).getTime();
      valueB = new Date(valueB).getTime();
    } else {
      valueA = String(valueA || "").toLowerCase();
      valueB = String(valueB || "").toLowerCase();
    }

    if (valueA < valueB) return sortingDescending ? 1 : -1;
    if (valueA > valueB) return sortingDescending ? -1 : 1;
    return 0;
  });

  const auditPagesCount = Math.ceil(sortedAuditItems.length / AUDIT_PAGE_SIZE);
  const auditStartIndex = (auditCurrentPage - 1) * AUDIT_PAGE_SIZE;
  const paginatedAuditItems = sortedAuditItems.slice(
    auditStartIndex,
    auditStartIndex + AUDIT_PAGE_SIZE
  );

  const renderUsersTable = () => (
    <>
      {loading ? (
        <Container>
          <Box textAlign="center" padding={{ bottom: "l" }}>
            <Spinner size="large" />
          </Box>
        </Container>
      ) : (
        <Table
          renderAriaLive={({ firstIndex, lastIndex, totalItemsCount }) =>
            `Displaying items ${firstIndex} to ${lastIndex} of ${totalItemsCount}`
          }
          onSelectionChange={({ detail }) =>
            setSelectedItems(detail.selectedItems)
          }
          selectedItems={selectedItems}
          ariaLabels={{
            selectionGroupLabel: "Items selection",
            allItemsSelectionLabel: () => "select all",
            itemSelectionLabel: ({ selectedItems }, item) => item.name,
          }}
          columnDefinitions={[
            {
              id: "name",
              header: t("users.name"),
              cell: (item) => item.name,
              sortingField: "name",
              isRowHeader: true,
            },
            {
              id: "email",
              header: t("users.email"),
              cell: (item) => item.email,
              sortingField: "email",
            },
            {
              id: "role",
              header: t("users.role"),
              cell: (item) => {
                if (!hasPermission("manage_users")) {
                  return item.role;
                }
                
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Select
                      selectedOption={roleOptions.find(
                        (option) => option.value === item.role
                      )}
                      onChange={(e) =>
                        handleRoleChange(item.name, e.detail.selectedOption.value)
                      }
                      options={roleOptions}
                      disabled={updatingUserRole && editingUserId === item.name}
                      expandToViewport={true}
                      autoFocus={false}
                      loadingText="Actualizando..."
                      statusType={updatingUserRole && editingUserId === item.name ? "loading" : "finished"}
                    />
                    {updatingUserRole && editingUserId === item.name && (
                      <Spinner size="normal" />
                    )}
                  </div>
                );
              },
              width: 180,
            },
            {
              id: "status",
              header: t("users.status"),
              cell: (item) => {
                switch (item.userStatus) {
                  case "FORCE_CHANGE_PASSWORD":
                    return t("users.changePassword");
                  case "CONFIRMED":
                    return t("users.confirmed");
                  default:
                    return item.userStatus;
                }
              },
            },
            {
              id: "actions",
              header: t("users.actions"),
              cell: (item) => (
                <Button
                  iconName="remove"
                  variant="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    setUserToDelete(item);
                    setShowModal(true);
                  }}
                />
              ),
              minWidth: 170,
            },
          ]}
          columnDisplay={[
            { id: "name", visible: true },
            { id: "email", visible: true },
            { id: "role", visible: true },
            { id: "status", visible: true },
            { id: "actions", visible: true },
          ]}
          enableKeyboardNavigation
          items={paginatedItems}
          loadingText="Cargando"
          stickyColumns={{ first: 0, last: 1 }}
          trackBy="name"
          empty={
            <Box margin={{ vertical: "xs" }} textAlign="center" color="inherit">
              <SpaceBetween size="m">
                <b>{t("users.noResources")}</b>
                <Button onClick={() => setShowAddUserModal(true)}>
                  {t("users.addUser")}
                </Button>
              </SpaceBetween>
            </Box>
          }
          filter={
            <TextFilter
              filteringPlaceholder={t("users.searchPlaceholder")}
              filteringText={filteringText}
              onChange={({ detail }) => setFilteringText(detail.filteringText)}
              countText={
                filteredItems.length > 0
                  ? `${filteredItems.length} ${
                      filteredItems.length === 1
                        ? t("users.result")
                        : t("users.results")
                    }`
                  : t("users.noResults")
              }
            />
          }
          header={
            <Header
              counter={`(${tableItems.length})`}
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  <Button
                    onClick={() => {
                      setNewUser({ name: "", email: "", role: "Invitado" });
                      setNewUserErrors({ name: "", email: "" });
                      setShowAddUserModal(true);
                    }}
                    variant="primary"
                  >
                    {t("users.addUser")}
                  </Button>
                </SpaceBetween>
              }
            >
              {t("users.usersList")}
            </Header>
          }
          pagination={
            <Pagination
              currentPageIndex={currentPageIndex}
              onChange={({ detail }) =>
                setCurrentPageIndex(detail.currentPageIndex)
              }
              pagesCount={pagesCount}
              ariaLabels={{
                nextPageLabel: t("pagination.next"),
                previousPageLabel: t("pagination.previous"),
                pageLabel: (pageNumber) => `${pageNumber} de ${pagesCount}`,
              }}
            />
          }
        />
      )}
    </>
  );

  const renderFeatureFlagsTable = () => (
    <Container>
      <Header
        variant="h2"
        description="Gestiona qué funcionalidades están disponibles para cada grupo de usuarios"
        actions={
          <Button
            iconName="refresh"
            onClick={() => {
              fetchFeatureFlags();
              fetchAuditLog();
            }}
            loading={loadingFeatureFlags}
          >
            Actualizar
          </Button>
        }
      >
        Gestión de Permisos por Rol
      </Header>

      {loadingFeatureFlags ? (
        <Box textAlign="center" padding={{ vertical: "l" }}>
          <Spinner size="large" />
        </Box>
      ) : (
        <Table
          columnDefinitions={[
            {
              id: "feature",
              header: "Funcionalidad",
              cell: (feature) => (
                <Box>
                  <div style={{ fontWeight: "bold" }}>{feature.name}</div>
                  <div style={{ fontSize: "12px", color: "#666" }}>
                    {feature.description}
                  </div>
                </Box>
              ),
              width: 300,
              isRowHeader: true,
            },
            ...roleOptions.map((role) => ({
              id: role.value,
              header: role.label,
              cell: (feature) => {
                const isAdmin = role.value === "Admin";
                const isChecked = isAdmin
                  ? true
                  : featureFlags[role.value]?.permissions?.includes(
                      feature.id
                    ) || false;

                return (
                  <Toggle
                    checked={isChecked}
                    onChange={({ detail }) =>
                      handlePermissionToggle(
                        role.value,
                        feature.id,
                        detail.checked
                      )
                    }
                    disabled={updatingPermissions || isAdmin}
                  />
                );
              },
              width: 120,
            })),
          ]}
          items={availableFeatures}
          loadingText="Cargando permisos..."
          loading={updatingPermissions}
          trackBy="id"
          variant="embedded"
        />
      )}
    </Container>
  );

  const renderAuditLog = () => (
    <Container>
      <Header 
        variant="h2" 
        description="Historial completo de cambios en permisos del sistema"
        counter={`(${auditLog.length})`}
        actions={
          <Button
            iconName="refresh"
            onClick={fetchAuditLog}
            loading={loadingAudit}
          >
            Actualizar
          </Button>
        }
      >
        Log de Auditoría
      </Header>

      {loadingAudit ? (
        <Box textAlign="center" padding={{ vertical: "l" }}>
          <Spinner size="large" />
        </Box>
      ) : (
        <Table
          columnDefinitions={[
            {
              id: "timestamp",
              header: "Fecha/Hora",
              cell: (item) => {
                const date = new Date(item.timestamp);
                return (
                  <div>
                    <div style={{ fontWeight: "bold" }}>
                      {date.toLocaleDateString('es-ES')}
                    </div>
                    <div style={{ fontSize: "12px", color: "#666" }}>
                      {date.toLocaleTimeString('es-ES')}
                    </div>
                  </div>
                );
              },
              sortingField: "timestamp",
              width: 140,
            },
            {
              id: "user",
              header: "Usuario",
              cell: (item) => item.user || "Sistema",
              sortingField: "user",
              width: 120,
            },
            {
              id: "role",
              header: "Rol Afectado",
              cell: (item) => <Badge color="blue">{item.role}</Badge>,
              sortingField: "role",
              width: 120,
            },
            {
              id: "action",
              header: "Acción",
              cell: (item) => (
                <StatusIndicator type={getActionStatusType(item.action)}>
                  {formatAuditAction(item.action)}
                </StatusIndicator>
              ),
              sortingField: "action",
              width: 150,
            },
            {
              id: "permission",
              header: "Permiso",
              cell: (item) => (
                <div style={{ 
                  fontFamily: "monospace", 
                  fontSize: "12px",
                  padding: "4px 8px",
                  backgroundColor: "#f6f6f6",
                  borderRadius: "4px",
                  maxWidth: "200px",
                  wordBreak: "break-word"
                }}>
                  {item.permission}
                </div>
              ),
              sortingField: "permission",
              width: 220,
            },
          ]}
          items={paginatedAuditItems}
          loadingText="Cargando auditoría..."
          trackBy="id"
          variant="embedded"
          sortingColumn={auditSortingColumn.sortingColumn}
          sortingDescending={auditSortingColumn.sortingDescending}
          onSortingChange={({ detail }) => setAuditSortingColumn(detail)}
          filter={
            <TextFilter
              filteringPlaceholder="Buscar en auditoría..."
              filteringText={auditFilteringText}
              onChange={({ detail }) => setAuditFilteringText(detail.filteringText)}
              countText={
                filteredAuditItems.length > 0
                  ? `${filteredAuditItems.length} ${
                      filteredAuditItems.length === 1
                        ? "registro"
                        : "registros"
                    }`
                  : "No hay registros"
              }
            />
          }
          pagination={
            <Pagination
              currentPageIndex={auditCurrentPage}
              onChange={({ detail }) => setAuditCurrentPage(detail.currentPageIndex)}
              pagesCount={auditPagesCount}
              ariaLabels={{
                nextPageLabel: "Página siguiente",
                previousPageLabel: "Página anterior",
                pageLabel: (pageNumber) => `Página ${pageNumber} de ${auditPagesCount}`,
              }}
            />
          }
          empty={
            <Box textAlign="center" padding={{ vertical: "l" }}>
              <SpaceBetween size="m">
                <b>No hay registros de auditoría disponibles</b>
                <Button onClick={fetchAuditLog} iconName="refresh">
                  Actualizar
                </Button>
              </SpaceBetween>
            </Box>
          }
        />
      )}
    </Container>
  );

  const tabs = [
    {
      id: "users",
      label: "Usuarios",
      content: renderUsersTable(),
    },
  ];

  if (hasPermission("manage_users")) {
    tabs.push(
      {
        id: "permissions",
        label: "Permisos",
        content: renderFeatureFlagsTable(),
      },
      {
        id: "audit",
        label: "Auditoría",
        content: renderAuditLog(),
      }
    );
  }

  return (
    <ContentLayout
      header={<Header variant="h1">{t("utilities.users")}</Header>}
    >
      <Container>
        <AlertNotification alert={alert} clearAlert={clearAlert} t={t} />

        <Tabs
          activeTabId={activeTabId}
          onChange={({ detail }) => setActiveTabId(detail.activeTabId)}
          tabs={tabs}
        />
      </Container>

      {showModal && (
        <Modal
          visible={showModal}
          onDismiss={() => setShowModal(false)}
          header={t("confirmModal.title")}
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button onClick={() => setShowModal(false)} variant="link">
                  {t("confirmModal.cancel")}
                </Button>
                <Button
                  onClick={handleDelete}
                  variant="primary"
                  loading={isDeletingUser}
                >
                  {t("confirmModal.confirm")}
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          <p>{t("confirmModal.message")}</p>
        </Modal>
      )}

      {showAddUserModal && (
        <Modal
          visible={showAddUserModal}
          onDismiss={() => setShowAddUserModal(false)}
          header="Agregar usuario"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  onClick={() => setShowAddUserModal(false)}
                  variant="link"
                >
                  {t("confirmModal.cancel")}
                </Button>
                <Button
                  onClick={handleAddUser}
                  variant="primary"
                  loading={isAddingUser}
                >
                  {t("confirmModal.add")}
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          <SpaceBetween size="l">
            <Input
              value={newUser.name}
              onChange={(e) => handleInputChange("name", e.detail.value)}
              placeholder="Nombre de usuario"
              label="Nombre"
              invalid={!!newUserErrors.name}
              errorText={newUserErrors.name}
            />
            <Input
              value={newUser.email}
              onChange={(e) => handleInputChange("email", e.detail.value)}
              placeholder="Correo electrónico"
              label="Email"
              invalid={!!newUserErrors.email}
              errorText={newUserErrors.email}
            />
            <Select
              selectedOption={roleOptions.find(
                (option) => option.value === newUser.role
              )}
              onChange={(e) =>
                handleInputChange("role", e.detail.selectedOption.value)
              }
              options={roleOptions}
              label="Rol"
            />
          </SpaceBetween>
        </Modal>
      )}
    </ContentLayout>
  );
}

export default Users;